import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

interface ColumnItem {
    name: string;
    sortable?: boolean;
    customFormatting?: (value: string | number) => JSX.Element
    customClassName?: string;
}

interface TableProps {
    columns: Array<string | ColumnItem>;
    data: Array<Array<string | number>>;
    rowClassName?: (rowIndex: number) => string|null;
    defaultSortColumn?: string | null;
    defaultSortDirection?: "asc" | "desc";
    headerClassName?: string;
}

const BasicTable: React.FC<TableProps> = ({
    columns,
    data,
    rowClassName,
    defaultSortColumn = null,
    defaultSortDirection = "desc",
    headerClassName = "px-4 py-2 border border-gray-300 text-left font-medium text-gray-700",
}) => {
    // update the columns to be an array of ColumnItem
    const parsedColumns = columns.map((col) => {
        let item = {} as ColumnItem;
        if (typeof col === "string") {
            item.name = col;
            item.sortable = true;
        } else {
            item = col as ColumnItem;
            item.sortable = item.sortable ?? true;
        }
        return item;
    }) as ColumnItem[];

    const [sortColumn, setSortColumn] = React.useState<string | null>(defaultSortColumn || null);
    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(defaultSortDirection);
    const [sortedData, setSortedData] = React.useState(data);

    const getSortSymbol = (column: string) => {
        if (sortColumn === column) {
            return sortDirection === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
        }
        return <></>;
    };

    const handleSort = (column: string, invertDirection: boolean = true) => {
        const columnItem = parsedColumns.find((col: ColumnItem) => col.name === column);
        if (!columnItem || !columnItem.sortable) return;

        setSortColumn(column);

        let _direction = sortDirection;
        if (invertDirection) {
            _direction = sortColumn === column && _direction === "asc" ? "desc" : "asc";
            setSortDirection(_direction);
        }

        const newData = [...data].sort((a, b) => {
            let aValue = a[parsedColumns.findIndex((col) => col.name === column)];
            let bValue = b[parsedColumns.findIndex((col) => col.name === column)];

            // If more symbols need to be handled, setup an array of characters to scrub for sorting
            if (typeof aValue === "string" && (aValue.includes("$") || aValue.includes("%"))) {
                aValue = parseFloat(aValue.replace(/[$,%]/g, ""));
            }
            if (typeof bValue === "string" && (bValue.includes("$") || bValue.includes("%"))) {
                bValue = parseFloat(bValue.replace(/[$,%]/g, ""));
            }

            if (typeof aValue === "string" && typeof bValue === "string") {
                return _direction === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return _direction === "asc" ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
            }
        });

        setSortedData(newData);
    };

    React.useEffect(() => {
        if (defaultSortColumn) {
            handleSort(defaultSortColumn, false);
        }
    }, [defaultSortColumn]);

    React.useEffect(() => {
        if (sortColumn) handleSort(sortColumn, false);
        else setSortedData(data);
    }, [data]);

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                    <tr className="bg-gray-100">
                        {parsedColumns.map((column, index) => {
                            let finalHeaderClassName = headerClassName;
                            if (column.sortable) {
                                finalHeaderClassName += " cursor-pointer";
                            }

                            let spanStyle = "flex"
                            if (finalHeaderClassName.includes("text-left")) {
                                spanStyle += " justify-start"
                            } else if (finalHeaderClassName.includes("text-center")) {
                                spanStyle += " justify-center"
                            } else if (finalHeaderClassName.includes("text-right")) {
                                spanStyle += " justify-end"
                            }

                            return (
                                <th
                                    key={index}
                                    className={finalHeaderClassName}
                                    onClick={() => column.sortable && handleSort(column.name)}
                                >
                                    <span className={spanStyle}>
                                        {column.name}
                                        {column.sortable && (
                                            <span className="ml-2 text-gray-500">
                                                {getSortSymbol(column.name)}
                                            </span>
                                        )}
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((row, rowIndex) => {
                        const customClassNameReturn = rowClassName ? rowClassName(rowIndex) : null;
                        let computedRowClassName = customClassNameReturn ? customClassNameReturn : (rowIndex % 2 === 0 ? 'bg-gray-50 hover:bg-gray-200' : 'hover:bg-gray-200');
                        return (
                            <tr
                                key={rowIndex}
                                className={computedRowClassName}
                            >
                                {row.map((cell, cellIndex) => {
                                    const column = parsedColumns[cellIndex];
                                    const formattedCell = column.customFormatting ?
                                        column.customFormatting(cell) :
                                        cell;

                                    let className;
                                    if (!customClassNameReturn) {
                                        className = column.customClassName ? column.customClassName : "px-4 py-2 border border-gray-300 text-gray-700";
                                    } else {
                                        className = "px-4 py-2 border"
                                    }

                                    return (
                                        <td
                                            key={cellIndex}
                                            className={className}
                                        >
                                            {formattedCell}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default BasicTable;